import React, { useState } from 'react'
import { submitFeedback } from '../api/feedbackAPI'
import {
  Box,
  Typography,
  Modal,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material'

interface OutdatedQuestionsFeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  scanResultId: number | null
  orgUuid: string
  onFeedbackSubmitted?: () => void
  token: string
}

const OutdatedQuestionsFeedbackModal: React.FC<
  OutdatedQuestionsFeedbackModalProps
> = ({
  isOpen,
  onClose,
  scanResultId,
  orgUuid,
  onFeedbackSubmitted,
  token,
}) => {
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) {
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setError(null)
    setIsSubmitting(true)

    try {
      await submitFeedback({
        scan_result_id: scanResultId,
        org_uuid: orgUuid,
        subject: 'Funder updated their questions',
        category: 'outdated_questions',
        message: message.trim() || 'No additional details provided',
        hidden: true, // Hide the match by default when giving feedback
        token,
      })

      // Reset form
      setMessage('')

      // Close modal and notify parent
      onClose()
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted()
      }
    } catch (err) {
      setError('Failed to submit feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="outdated-questions-modal"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '500px',
          maxWidth: '90vw',
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          px: 4,
          borderRadius: 2,
          pt: 3,
          pb: 3,
        }}
      >
        <Typography
          id="outdated-questions-modal"
          variant="h5"
          component="h2"
          sx={{ textAlign: 'center', mb: 2, fontWeight: 500 }}
        >
          Report Outdated Questions
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Let us know if the funder has updated their questions and we'll look
          into it.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              Error submitting feedback
            </Typography>
            <Typography variant="body2">{error}</Typography>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              If this problem persists, please contact support.
            </Typography>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Additional details (optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSubmitting}
            placeholder="Please provide any details that might help..."
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#757575',
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              disableRipple
              onClick={onClose}
              disabled={isSubmitting}
              sx={{
                borderColor: '#1C3045',
                border: 2,
                borderRadius: 2,
                color: '#1C3045',
                textTransform: 'none',
                fontSize: '16px',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              disableRipple
              variant="contained"
              type="submit"
              disabled={isSubmitting}
              sx={{
                bgcolor: '#1C3045',
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '16px',
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                  <span>Submitting...</span>
                </Box>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  )
}

export default OutdatedQuestionsFeedbackModal
